import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Typography, Alert } from '@mui/material';
import { GmailAccountsList } from '../../../components/settings/gmail/GmailAccountsList';
import { ConnectGmailButton } from '../../../components/settings/gmail/ConnectGmailButton';
import { gmailApi } from '../../../lib/api/gmail';
import { useAuth0 } from '@auth0/auth0-react';
import { GmailAccountDTO } from '../../../types/gmail';
import { SettingsLayout } from '../../../components/settings/SettingsLayout';

export default function GmailSettingsPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();
  const [accounts, setAccounts] = useState<GmailAccountDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for success message in location state
    if (location.state?.success && location.state?.message) {
      setSuccess(location.state.message);
      // Clear the success message from location state after displaying
      window.history.replaceState({}, document.title);
    }
    loadAccounts();
  }, [orgId]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const accounts = await gmailApi.listAccounts(orgId!, token);
      setAccounts(accounts);
      setError(null);
    } catch (err: any) {
      console.error('Error loading Gmail accounts:', err);
      setError(err.message || 'Failed to load Gmail accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountConnected = (account: GmailAccountDTO) => {
    setAccounts([...accounts, account]);
    setSuccess(`Successfully connected ${account.email}`);
  };

  const handleAccountDisconnected = async (accountId: string) => {
    try {
      const account = accounts.find(a => a.id === accountId);
      if (!account) return;

      setAccounts(accounts.filter(a => a.id !== accountId));
      setSuccess(`Successfully disconnected ${account.email}`);
      setError(null);
    } catch (err: any) {
      console.error('Error disconnecting account:', err);
      setError(err.message || 'Failed to disconnect account');
    }
  };

  const handleSetDefault = async (accountId: string) => {
    try {
      const token = await getAccessTokenSilently();
      await gmailApi.setDefaultAccount(orgId!, accountId, token);
      
      // Reload accounts to get updated default status
      await loadAccounts();
      
      const account = accounts.find(a => a.id === accountId);
      if (account) {
        setSuccess(`Set ${account.email} as default account`);
        setError(null);
      }
    } catch (err: any) {
      console.error('Error setting default account:', err);
      setError(err.message || 'Failed to set default account');
    }
  };

  return (
    <SettingsLayout>
      <div className="bg-gray-800 rounded-lg shadow-xl p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-100 mb-4">
          Connected Accounts
        </h2>
        
        {error && (
          <div className="mb-4 p-4 bg-red-900/50 border border-red-500/50 rounded-md text-red-200">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-900/50 border border-green-500/50 rounded-md text-green-200">
            {success}
            <button 
              onClick={() => setSuccess(null)}
              className="float-right text-green-200 hover:text-green-100"
            >
              ×
            </button>
          </div>
        )}

        <div className="mb-6">
          <ConnectGmailButton
            orgId={orgId}
            onAccountConnected={handleAccountConnected}
          />
        </div>

        <GmailAccountsList
          accounts={accounts}
          loading={loading}
          onSetDefault={handleSetDefault}
          onDisconnect={handleAccountDisconnected}
        />
      </div>

      <div className="bg-gray-800 rounded-lg shadow-xl p-6">
        <h2 className="text-xl font-semibold text-gray-100 mb-4">
          About Gmail Integration
        </h2>
        <p className="text-gray-300">
          Connect your Gmail accounts to send emails directly from the platform.
          You can connect multiple accounts and set a default account for sending emails.
          All emails will be sent through your connected Gmail accounts, maintaining
          your brand identity and improving deliverability.
        </p>
      </div>
    </SettingsLayout>
  );
} 